import moment from 'moment-timezone';
import get from 'lodash/get';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  next,
  previous,
  reset,
  setDate,
  setError,
  setScheduleManually,
  setScreenIndex,
  setSessionTypeId,
  setTime
} from '../../redux/reducers/newAppointmentForm';
import {useGetAvailabilitiesForMonthQuery, useGetAvailabilitiesQuery} from '../../redux/services/availabilityApi';
import { useGetProvidersQuery } from '../../redux/services/providerApi';
import { formatProviderName, getProfileImage } from '../../helpers/providers';
import FormLayout from "../patientSignup/FormLayout";
import SelectSessionTypeForm from './SelectSessionTypeForm';
import SelectDateForm from './SelectDateForm';
import ScheduleManuallyScreen from './ScheduleManuallyScreen';
import BackButton from "../common/BackButton";

const getScreens = ({props, onFormSubmit}) => ([
  {
    key: 'selectSessionType',
    title: 'Select a Session Type',
    subtitle: null,
    buttonText: null,
    component: SelectSessionTypeForm,
    onSubmit: sessionTypeId => {
      if (!sessionTypeId) {
        return props.setError({ message: 'Please select a sessionType' });
      }
      props.setError(null);
      props.setSessionTypeId(sessionTypeId);
      props.nextForm();
    }
  },
  {
    key: 'selectDay',
    component: SelectDateForm,
    onSubmit: (date, time) => {
      if (!date) return props.setError({message: 'Please enter a valid date'});
      if (!time) return props.setError({message: 'Please select a time'});
      const dateStr = date.format('MM/DD/yyyy');
      if (!dateStr) return props.setError({message: 'Please select a date'});
      if (dateStr === 'Invalid date') {
        return props.setError({message: 'Please enter a valid date'});
      }
      props.setError(null);
      props.setDate(dateStr);
      props.setTime(time);
      props.nextForm();
      onFormSubmit();
    }
  }
]);

const getAvailabilityTimeRange = (selectedDate, timezone) => {
  if (!timezone) return {startTime: null, endTime: null};
  if (selectedDate) {
    const startTime = moment(selectedDate, 'MM/DD/yyyy')
      .endOf('day')
      .tz(timezone)
      .startOf('day')
      .toISOString();
    const endTime = moment(selectedDate, 'MM/DD/yyyy')
      .endOf('day')
      .tz(timezone)
      .endOf('day')
      .toISOString();
    return {startTime, endTime};
  }
  const startTime = moment()
    .endOf('day')
    .tz(timezone)
    .startOf('day')
    .toISOString();
  const endTime = moment(selectedDate, 'MM/DD/yyyy')
    .endOf('day')
    .tz(timezone)
    .endOf('day')
    .toISOString();
  return {startTime, endTime};
};

const getAvailableDates = ({timezone}) => availabilitiesQuery => {
  const availabilities = get(availabilitiesQuery, 'data.available_slots', []);
  const dateDict = availabilities.reduce((acc, next) => {
    const dateStr = moment(next.start_time)
      .tz(timezone || moment.tz.guess())
      .format('MM-DD-yyyy');
    return {...acc, [dateStr]: true};
  }, {});
  const uniqueDates = Object.keys(dateDict);
  return uniqueDates;
};

const getAvailableTimes = ({selectedDate, availabilitiesQuery, timezone}) => {
  if (!selectedDate) return [];
  const availabilities = get(availabilitiesQuery, 'data.available_slots', []);
  const dateStr = moment(selectedDate).format('yyyy-MM-DD');
  // output times as hh:mm a like ['9:00 am', ...]
  const formattedTimes = availabilities
    // filter out times on other days
    .filter(a => moment(a.start_time).tz(timezone).format('yyyy-MM-DD') === dateStr)
    .map(a => moment(a.start_time).tz(timezone).format('h:mm a'));
  return formattedTimes;
};

const handlePrevMonth = ({monthNum, setMonthNum, year, setYear}) => () => {
  if (monthNum - 1 === 0) {
    setMonthNum(12);
    setYear(year - 1);
    return;
  }
  setMonthNum(monthNum - 1);
};

const handleNextMonth = ({monthNum, setMonthNum, year, setYear}) => () => {
  if (monthNum + 1 === 13) {
    setMonthNum(1);
    setYear(year + 1);
    return;
  }
  setMonthNum(monthNum + 1);
};

const handleBack = ({
  props,
  screenIndex,
  setMonthNum,
  setYear,
  scheduleManually
}) => e => {
  if (props.date) {
    setMonthNum((new Date(props.date)).getMonth() + 1);
    setYear((new Date(props.date)).getFullYear());
  } else {
    setMonthNum((new Date()).getMonth() + 1);
    setYear((new Date()).getFullYear());
  }
  if (scheduleManually) {
    props.resetForm();
    props.setScheduleManually(false);
    return;
  }
  if (screenIndex === 0) {
    if (props.onPrevScreen) props.onPrevScreen();
    if (!props.onPrevScreen) props.prevForm();
    props.resetForm(); // reset form data
    return;
  }
  // if (screenIndex === 2) {
  //   const month = get(
  //     (props.date || '').match(/^[\d]{1,2}\//),
  //     '[0]',
  //     ''
  //   ).replace(/\//, '');
  //   const year = get(
  //     (props.date || '').match(/[\d]{4,4}$/),
  //     '[0]',
  //     ''
  //   );
  //   if (month && year) {
  //       setMonthNum(parseInt(month, 10));
  //       setYear(parseInt(year, 10));
  //       props.prevForm();
  //       return;
  //   }
  // }
  props.prevForm();
};

const handleScheduleManually = ({props}) => () => {
  props.setDate(null);
  props.setTime(null);
  props.setScheduleManually(true);
};

const NewAppointmentForm = props => {
  const {
    screenIndex,
    sessionTypeId,
    scheduleManually,
    selectedProviderId,
    hideBack,
    isNewPatient,
    backButtonPlacement = 'bottom',
  } = props;
  const [monthNum, setMonthNum] = useState(
    props.date
    ? (new Date(props.date)).getMonth() + 1
    : (new Date()).getMonth() + 1
  );
  const [year, setYear] = useState(
    props.date
    ? (new Date(props.date)).getFullYear()
    : (new Date()).getFullYear()
  );
  const [caseId, setCaseId] = useState(props.defaultCaseId);
  const providerQuery = useGetProvidersQuery(
    {id: selectedProviderId}, 
    {skip: !selectedProviderId}
  );
  const provider = providerQuery?.data?.[0];
  const timezone = provider?.timezone;
  const {startTime, endTime} = getAvailabilityTimeRange(props.date, timezone);
  const availabilitiesQuery = useGetAvailabilitiesQuery(
    {
      providerId: selectedProviderId,
      session_type_id: sessionTypeId,
      start_time: startTime,
      end_time: endTime
    },
    {
      skip: !selectedProviderId || !sessionTypeId || !timezone || !startTime || !endTime,
      refetch: true,
      pollingInterval: 120000,
    }
  );
  const monthQuery = {
    monthNum,
    year,
    providerId: selectedProviderId,
    session_type_id: sessionTypeId,
  };
  const monthAvailabilitiesQuery = useGetAvailabilitiesForMonthQuery(
    monthQuery,
    {
      skip: !selectedProviderId || !sessionTypeId || !monthNum,
      refetch: true,
      pollingInterval: 120000,
    }
  );
  useEffect(() => {
    if (availabilitiesQuery.data && availabilitiesQuery.isLoaded) {
      availabilitiesQuery.refetch();
    }
  }, [startTime, endTime, sessionTypeId, availabilitiesQuery, timezone]);
  useEffect(() => {
    if (monthAvailabilitiesQuery.data && monthAvailabilitiesQuery.isLoaded) {
      monthAvailabilitiesQuery.refetch();
    }
  }, [year, monthNum, monthAvailabilitiesQuery, timezone]);
  let formValues = {
    ...pick(
      props,
      'time', 'date', 'scheduleManually', 'sessionTypeId', 'selectedProviderId'
    ),
    case_id: caseId,
  };
  const screens = getScreens({
    props,
    onFormSubmit: () => props.onSubmit(formValues),
  });
  const sessionType = (
    sessionTypeId
    ? (get(props, 'sessionTypes') || []).find(s => s.id === sessionTypeId)
    : null
  );
  const hideAvailability = get(sessionType, 'hide_availability', false);
  const onScheduleManually = handleScheduleManually({props});
  const screen = get(screens, screenIndex);
  const Screen = screen.component;
  const onBack = handleBack({
    props,
    screenIndex,
    setMonthNum,
    setYear,
    scheduleManually: scheduleManually || hideAvailability,
  });

  const hideBackButton = () => props.hideBack || (screenIndex === 0 && !props.onPrevScreen);

  const childProps = {
    ...props,
    sessionType,
    appointmentStartTimes: getAvailableTimes({
      availabilitiesQuery,
      timezone,
      selectedDate: props.date,
    }),
    isLoading: (
      monthAvailabilitiesQuery.isFetching
      || availabilitiesQuery.isFetching
    ),
    dates: getAvailableDates({timezone})(monthAvailabilitiesQuery),
    onScheduleManually,
    onSubmit: screen.onSubmit,
    onBack: hideBack ? null : onBack,
    onDateChange: dateObj => {
      const dateStr = dateObj.format('MM/DD/yyyy');
      // console.log('onDateChange', dateStr);
      props.setDate(dateStr);
    },
    onTimeChange: time => props.setTime(time),
    onCaseChange: e => setCaseId(e?.target?.value),
    onPrevMonthClick: handlePrevMonth({monthNum, setMonthNum, year, setYear}),
    onNextMonthClick: handleNextMonth({monthNum, setMonthNum, year, setYear}),
    timezone,
    isNewPatient,
  };
  return (
    <Box style={{width: '100%'}}>
      <FormLayout marginTop={0}>
        {
          backButtonPlacement === 'top' && onBack && !hideBackButton()
          && (
          <Stack alignItems='flex-start'>
            <Button onClick={onBack} size='small'>
              <Typography variant='large' lineHeight='1.875rem'>{'<'} Back</Typography>
            </Button>
          </Stack>
          )
        }
        <Stack alignItems='center' marginTop={0}>
          {/* <Box style={{width: '100%'}}>
            <Stack direction='row' padding={0.5}>
              <Stack gap={0.25}>
                <Typography>
                  {formatProviderName(props.selectedProvider)}
                </Typography>
              </Stack>
            </Stack>
          </Box> */}
          {
            props.error
            ? <Alert severity='error'>{props.error.message}</Alert>
            : null
          }
          {
            props.showProviderInfo && provider
            ? <Container maxWidth='xs' style={{padding: '0px'}}>
              <Stack 
                direction='row' 
                spacing={2} 
                justifyContent='flex-start' 
                alignItems='center'
                padding={0}
                paddingBottom={2}
              >
                <Avatar
                  alt={formatProviderName(provider)}
                  src={getProfileImage(provider)}
                />
                <Typography variant='h3' color='text.light'>
                  {formatProviderName(provider)}
                </Typography>
              </Stack>
            </Container>
            : null
          }
          {
            scheduleManually || hideAvailability
            ? <ScheduleManuallyScreen
              sessionType={sessionType}
              selectedProvider={props.selectedProvider}
              onContinue={() => {
                onScheduleManually();
                if (props?.onScheduleManually) props.onScheduleManually();
                else if (props.onSubmit) props.onSubmit()
              }}
              onScheduleNow={onBack}
              selectedPatient={props.selectedPatient}
              scheduleManuallyMessage={props?.scheduleManuallyMessage}
              hideBodyText={!!props?.hideBodyText}
              hideScheduleAutomaticallyButton={!!props?.hideScheduleAutomaticallyButton}
            />
            : <Screen {...childProps} />
          }
          {
            props?.onMessageProvider
            ? <Button
              onClick={props.onMessageProvider}
            >
              Message Provider to Schedule
            </Button>
            : null
          }
        </Stack>

        {
          backButtonPlacement === 'bottom' && onBack && !hideBackButton()
          && (
            <Button onClick={onBack} sx={{alignSelf: 'flex-start', marginLeft: '12px'}}>
              <Typography variant='large' lineHeight='1.875rem' color='white'>{'<'} Back</Typography>
            </Button>
          )
        }
      </FormLayout>
    </Box>
  );
};

NewAppointmentForm.defaultProps = {
  hideBack: false,
  showProviderInfo: false,
  isNewPatient: false,
};

NewAppointmentForm.propTypes = {
  // inputs from parent
  selectedProviderId: PropTypes.string.isRequired,
  selectedProvider: PropTypes.array,
  selectedPatient: PropTypes.object,
  // e.g.: ({time: '9:00 AM', date: '10/30/2023'}) =>
  isProvider: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  step: PropTypes.func, // optional
  numSteps: PropTypes.func, // optional
  onPrevScreen: PropTypes.func,
  sessionTypes: PropTypes.array,
  hideStepper: PropTypes.bool,
  isNewPatient: PropTypes.bool,
  // redux data
  screenIndex: PropTypes.number,
  scheduleManually: PropTypes.bool,
  sessionTypeId: PropTypes.string,
  time: PropTypes.string,
  date: PropTypes.string,
  error: PropTypes.object,
  // redux handlers
  setScheduleManually: PropTypes.func.isRequired,
  setScreenIndex: PropTypes.func.isRequired,
  setSessionTypeId: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setTime: PropTypes.func.isRequired,
  // nextForm: PropTypes.func.isRequired,
  // prevForm: PropTypes.func.isRequired,
  // resetForm: PropTypes.func.isRequired,
  backButtonPlacement: PropTypes.string,
  onScheduleManually: PropTypes.func,
  scheduleManuallyMessage: PropTypes.string,
  showProviderInfo: PropTypes.bool,
};

const mapState = state => ({
  screenIndex: get(state, 'newAppointmentForm.screenIndex', 0),
  scheduleManually: get(state, 'newAppointmentForm.scheduleManually', false),
  sessionTypeId: get(state, 'newAppointmentForm.sessionTypeId', null),
  date: get(state, 'newAppointmentForm.date', null),
  time: get(state, 'newAppointmentForm.time', null),
  error: get(state, 'newAppointmentForm.error', null),
});

const mapDispatch = dispatch => ({
  setScheduleManually: x => dispatch(setScheduleManually(x)),
  setScreenIndex: x => dispatch(setScreenIndex(x)),
  setSessionTypeId: x => dispatch(setSessionTypeId(x)),
  setDate: x => dispatch(setDate(x)),
  setError: x => dispatch(setError(x)),
  setTime: x => dispatch(setTime(x)),
  nextForm: x => dispatch(next(x)),
  prevForm: x => dispatch(previous(x)),
  resetForm: x => dispatch(reset(x)),
});

export default connect(mapState,mapDispatch)(NewAppointmentForm);
